/**
 * Cloudflare Turnstile integration for the Supabase Auth flows.
 *
 * Supabase verifies the captcha token server-side once captcha is enabled in
 * the dashboard (Authentication > Attack Protection). Our only job on the
 * client is to obtain a token and hand it to the matching auth call.
 *
 * Everything here is a no-op when VITE_TURNSTILE_SITE_KEY is unset: no script
 * tag, no widget, no token, and every auth form behaves exactly as it did
 * before Turnstile existed. That keeps local dev and Vercel previews working
 * before the key is deployed.
 *
 * Tokens are single use and expire after a few minutes, so the controller
 * returned by useTurnstile() is built around reset(): call it after every
 * submit attempt, success or failure, and the widget issues a fresh token for
 * the next one.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { reportError } from '@/lib/reportError';

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * Give up on the script after this long. Without it, a request that is hung or
 * throttled rather than cleanly failed leaves the promise pending forever, and
 * the form sits there with an empty widget and no explanation.
 */
const SCRIPT_TIMEOUT_MS = 10_000;

/**
 * How long to call a token refresh "in progress" before falling back to asking
 * the user to complete the check. Bounded so the hint can never stick.
 */
const REFRESH_GRACE_MS = 6_000;

/** Shown when a form is submitted and the user has not completed the check. */
export const CAPTCHA_REQUIRED_MESSAGE = 'Please complete the verification and try again.';

/**
 * Shown when a submit lands in the second or two after the previous attempt,
 * while the widget is still minting a replacement token. Distinct from
 * CAPTCHA_REQUIRED_MESSAGE so a failed login retry does not read as if the user
 * skipped something. The widget shows its own spinner during this window, so
 * this is the only copy for it.
 */
export const CAPTCHA_REFRESHING_MESSAGE =
  'We are getting a fresh verification check. Give it a second, then try again.';

/**
 * Shown in place of the widget when the script could not load. Explains the
 * cause; the Try again control sits directly underneath it.
 */
export const CAPTCHA_BLOCKED_HINT =
  'The verification check could not load. An ad blocker or privacy extension blocking challenges.cloudflare.com is the usual cause.';

/**
 * Shown on the form when a submit is blocked because the widget never loaded.
 * Deliberately says something different from CAPTCHA_BLOCKED_HINT, since both
 * are on screen at once: the hint explains what happened, this explains what to
 * do about it.
 */
export const CAPTCHA_UNAVAILABLE_MESSAGE =
  'We cannot confirm you are human until the verification check loads. Use Try again, or email support@firstdeal.com and we will get you in.';

/** Shown when Supabase rejects a token we did send. */
export const CAPTCHA_REJECTED_MESSAGE =
  'We could not confirm the verification check. Please try again. If it keeps happening, email support@firstdeal.com and we will get you in.';

export interface TurnstileRenderOptions {
  sitekey: string;
  action?: string;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'flexible';
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'timeout-callback'?: () => void;
  'error-callback'?: (code?: string) => void;
}

export interface TurnstileApi {
  render: (container: HTMLElement | string, options: TurnstileRenderOptions) => string | undefined;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
  getResponse?: (widgetId?: string) => string | undefined;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/**
 * Vite statically replaces import.meta.env.VITE_* with a literal at build time,
 * so in a real build this is a constant no matter where it is read. Reading it
 * inside a function exists purely so component tests can stub the value with
 * vi.stubEnv, where import.meta.env stays a mutable object.
 */
function siteKey(): string {
  const raw = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  return (raw ?? '').trim();
}

/** True when a site key is configured. Everything captcha-related is off when false. */
// The component, its hook and these helpers are one self-contained unit.
// eslint-disable-next-line react-refresh/only-export-components
export function isCaptchaEnabled(): boolean {
  return siteKey().length > 0;
}

/**
 * Does this Supabase auth error mean the captcha token was refused?
 *
 * GoTrue surfaces these as raw strings such as
 * "captcha protection: request disallowed (invalid-input-response)".
 * invalid-input-response means the site key and secret do not match, the
 * provider is wrong, or the domain is not registered. timeout-or-duplicate
 * means the token was already spent or has aged out.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function isCaptchaRejection(message?: string | null): boolean {
  if (!message) return false;
  return /captcha|invalid-input-response|timeout-or-duplicate|invalid-input-secret/i.test(message);
}

/**
 * Pick the copy to show for a Supabase auth error, reporting captcha
 * rejections on the way through.
 *
 * A rejection almost always means the rollout is misconfigured rather than
 * anything the user did, and users only see a raw GoTrue string, so this is
 * the signal that tells us the switch was flipped wrong. Non-captcha errors
 * pass through untouched.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function authErrorMessage(rawMessage: string): string {
  if (!isCaptchaRejection(rawMessage)) return rawMessage;
  reportError(new Error(rawMessage), { tool: 'auth_captcha', endpoint: 'supabase-auth' });
  return CAPTCHA_REJECTED_MESSAGE;
}

let scriptPromise: Promise<void> | null = null;

/**
 * Inject the Turnstile script at most once per page, and only when a widget
 * actually mounts. Concurrent callers share one promise.
 *
 * A failed attempt clears both the cached promise and the dead <script>
 * element. Keeping the element around would poison every later mount: the next
 * caller would find it, attach listeners to a node that already finished
 * failing, and wait on a promise that can never settle.
 */
function loadTurnstileScript(): Promise<void> {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('Turnstile requires a browser document'));
  }
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    // Start from a clean element every time. Anything left in the head is from
    // an attempt that did not finish, and reusing it would hang.
    document.getElementById(SCRIPT_ID)?.remove();

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;

    let settled = false;

    const finish = (err?: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (!err) {
        resolve();
        return;
      }
      // Clear the cache and the element together so the next mount, or the
      // "Try again" control, issues a genuinely new request.
      scriptPromise = null;
      script.remove();
      reject(err);
    };

    const timer = setTimeout(
      () => finish(new Error(`Turnstile script did not load within ${SCRIPT_TIMEOUT_MS}ms`)),
      SCRIPT_TIMEOUT_MS,
    );

    script.addEventListener('load', () => finish());
    script.addEventListener('error', () => finish(new Error('Turnstile script failed to load')));

    document.head.appendChild(script);
  });

  return scriptPromise;
}

export type TurnstileStatus =
  | 'disabled'
  | 'pending'
  | 'solved'
  | 'expired'
  | 'refreshing'
  | 'unavailable';

/** Props the controller supplies to <Turnstile />. Spread them, do not build them by hand. */
export interface TurnstileBinding {
  onVerify: (token: string) => void;
  onExpire: () => void;
  onError: () => void;
  onRetry: () => void;
  registerReset: (reset: (() => void) | null) => void;
}

export interface TurnstileCheck {
  /** True when the flow may proceed. */
  ok: boolean;
  /** The token to forward to Supabase. Undefined when captcha is disabled. */
  token?: string;
  /** What to show the user. Empty string when ok is true. */
  message: string;
}

export interface TurnstileController {
  token: string | null;
  status: TurnstileStatus;
  /**
   * Gate a submit. Returns the token to forward when the flow may proceed, or
   * a plain-language message to show the user when it may not.
   */
  check: () => TurnstileCheck;
  /** Discard the current token and ask the widget for a new challenge. */
  reset: () => void;
  props: TurnstileBinding;
}

/**
 * Owns the token for one form. Pair it with a single <Turnstile /> instance:
 *
 *   const captcha = useTurnstile();
 *   ...
 *   const gate = captcha.check();
 *   if (!gate.ok) { setError(gate.message); return; }
 *   const result = await loginWithEmail(email, password, gate.token);
 *   captcha.reset();
 *   ...
 *   <Turnstile {...captcha.props} />
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useTurnstile(): TurnstileController {
  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState<TurnstileStatus>(
    isCaptchaEnabled() ? 'pending' : 'disabled',
  );
  const widgetResetRef = useRef<(() => void) | null>(null);

  const onVerify = useCallback((next: string) => {
    setToken(next);
    setStatus('solved');
  }, []);

  const onExpire = useCallback(() => {
    setToken(null);
    setStatus('expired');
  }, []);

  const onError = useCallback(() => {
    setToken(null);
    setStatus('unavailable');
  }, []);

  const onRetry = useCallback(() => {
    setToken(null);
    setStatus(isCaptchaEnabled() ? 'pending' : 'disabled');
  }, []);

  const registerReset = useCallback((fn: (() => void) | null) => {
    widgetResetRef.current = fn;
  }, []);

  const reset = useCallback(() => {
    setToken(null);
    if (!isCaptchaEnabled()) {
      setStatus('disabled');
      return;
    }
    // A live widget mints a replacement on its own within a moment. Say so,
    // rather than telling the user to complete a check that is already running.
    const widgetReset = widgetResetRef.current;
    setStatus(widgetReset ? 'refreshing' : 'pending');
    widgetReset?.();
  }, []);

  // Never leave the refreshing hint up indefinitely. If no token arrives, fall
  // back to asking the user to complete the check.
  useEffect(() => {
    if (status !== 'refreshing') return;
    const timer = setTimeout(() => {
      setStatus((prev) => (prev === 'refreshing' ? 'pending' : prev));
    }, REFRESH_GRACE_MS);
    return () => clearTimeout(timer);
  }, [status]);

  const check = useCallback((): TurnstileCheck => {
    if (!isCaptchaEnabled()) return { ok: true, message: '' };
    if (token) return { ok: true, token, message: '' };
    if (status === 'unavailable') return { ok: false, message: CAPTCHA_UNAVAILABLE_MESSAGE };
    if (status === 'refreshing') return { ok: false, message: CAPTCHA_REFRESHING_MESSAGE };
    return { ok: false, message: CAPTCHA_REQUIRED_MESSAGE };
  }, [token, status]);

  const props = useMemo<TurnstileBinding>(
    () => ({ onVerify, onExpire, onError, onRetry, registerReset }),
    [onVerify, onExpire, onError, onRetry, registerReset],
  );

  return { token, status, check, reset, props };
}

export interface TurnstileProps extends Partial<TurnstileBinding> {
  onVerify: (token: string) => void;
  className?: string;
  theme?: 'light' | 'dark' | 'auto';
  /** Optional label so Cloudflare analytics can tell the flows apart. */
  action?: string;
}

/**
 * Renders the Turnstile widget. Returns null when no site key is configured,
 * which is what makes the whole feature disappear in dev and preview.
 */
export function Turnstile({
  onVerify,
  onExpire,
  onError,
  onRetry,
  registerReset,
  className,
  theme = 'light',
  action,
}: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const key = siteKey();

  // Hold the latest callbacks in a ref so identity changes never tear down and
  // re-render the widget, which would throw away a solved token.
  const handlersRef = useRef({ onVerify, onExpire, onError });
  handlersRef.current = { onVerify, onExpire, onError };

  useEffect(() => {
    if (!key) return;

    let active = true;

    loadTurnstileScript()
      .then(() => {
        if (!active) return;
        const api = window.turnstile;
        if (!api || !containerRef.current) {
          setLoadFailed(true);
          handlersRef.current.onError?.();
          reportError(new Error('Turnstile script loaded but window.turnstile is missing'), {
            tool: 'auth_captcha',
            endpoint: 'cloudflare-turnstile',
          });
          return;
        }
        const id = api.render(containerRef.current, {
          sitekey: key,
          action,
          theme,
          callback: (nextToken: string) => {
            if (active) handlersRef.current.onVerify(nextToken);
          },
          'expired-callback': () => {
            if (active) handlersRef.current.onExpire?.();
          },
          'timeout-callback': () => {
            if (active) handlersRef.current.onExpire?.();
          },
          'error-callback': () => {
            if (active) handlersRef.current.onError?.();
          },
        });
        widgetIdRef.current = id ?? null;
        if (id) registerReset?.(() => window.turnstile?.reset(id));
      })
      .catch((err: unknown) => {
        if (!active) return;
        setLoadFailed(true);
        handlersRef.current.onError?.();
        reportError(err, { tool: 'auth_captcha', endpoint: 'cloudflare-turnstile' });
      });

    return () => {
      active = false;
      registerReset?.(null);
      const id = widgetIdRef.current;
      widgetIdRef.current = null;
      if (id) {
        try {
          window.turnstile?.remove(id);
        } catch {
          // The widget may already be gone if the script tore itself down.
        }
      }
    };
  }, [key, theme, action, registerReset, attempt]);

  if (!key) return null;

  const retry = () => {
    setLoadFailed(false);
    onRetry?.();
    setAttempt((n) => n + 1);
  };

  return (
    <div className={className}>
      <div ref={containerRef} data-testid="turnstile-widget" />
      {loadFailed && (
        <div className="mt-2 text-left">
          <p className="text-sm text-destructive">{CAPTCHA_BLOCKED_HINT}</p>
          <button
            type="button"
            onClick={retry}
            className="mt-1.5 text-sm font-semibold text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      )}
    </div>
  );
}
